import { CheckCircle, Phone, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOMeta from "@/components/SEOMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import OptimizedImage from "@/components/OptimizedImage";
import FAQ from "@/components/FAQ";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import modernKitchenIsland from "@/assets/modern-kitchen-quartz-island.jpg";
import openConceptKitchen from "@/assets/open-concept-kitchen-renovation.jpg";
import luxuryKitchen from "@/assets/luxury-custom-kitchen-niagara.jpg";
import smallKitchen from "@/assets/small-kitchen-makeover-condo.jpg";
import contemporaryKitchen from "@/assets/contemporary-kitchen-gray-cabinets.jpg";
import kitchenIsland from "@/assets/kitchen-island-waterfall-counter.jpg";

const KitchenRenovations = () => {

  const faqs = [
    {
      question: "What is a realistic budget for a kitchen remodel?",
      answer: "In Niagara, expect $25k–$50k+ CAD for a full kitchen remodel, depending on size, layout, and finishes. Compact refreshes can start around $12k–$25k, while luxury builds with custom cabinetry and stone countertops can exceed $100k."
    },
    {
      question: "Is $10,000 enough for a kitchen remodel?",
      answer: "It can be, if the project is cosmetic. Repainting cabinets, upgrading hardware, lighting, and countertops can fit within a $10k budget if the layout and plumbing remain unchanged."
    },
    {
      question: "What is the most expensive part of redoing a kitchen?",
      answer: "Cabinetry typically takes the largest portion of the budget, often 30–40%, followed by countertops and appliances. Custom layouts and stone surfaces add cost but bring long-term value."
    },
    {
      question: "How much would it cost to fully renovate a kitchen?",
      answer: "A full gut-to-studs kitchen renovation in Niagara typically runs $35k–$90k+ CAD, depending on space, materials, and upgrades (like islands, lighting, or wall removal)."
    },
    {
      question: "How long does a kitchen renovation take?",
      answer: "Most kitchens take 3–6 weeks, depending on complexity, cabinet lead times, and electrical/plumbing scope."
    },
    {
      question: "Do you offer design help?",
      answer: "Yes! We offer in-house kitchen design services, complete with 3D renderings and expert material guidance."
    }
  ];

  const galleryImages = [
    {
      src: modernKitchenIsland,
      alt: "Modern kitchen remodel with quartz island — St Catharines home",
      title: "Modern Kitchen with Quartz Island"
    },
    {
      src: openConceptKitchen,
      alt: "Open-concept kitchen renovation with pendant lighting — Niagara Falls remodel",
      title: "Open-Concept Kitchen Design"
    },
    {
      src: luxuryKitchen,
      alt: "Luxury kitchen renovation with waterfall counters — Welland ON",
      title: "Luxury Custom Kitchen"
    },
    {
      src: smallKitchen,
      alt: "Small kitchen makeover with efficient layout — Condo renovation Niagara",
      title: "Small Kitchen Makeover"
    },
    {
      src: contemporaryKitchen,
      alt: "Contemporary kitchen with LED lighting — Thorold ON renovation",
      title: "Contemporary Kitchen Design"
    },
    {
      src: kitchenIsland,
      alt: "Kitchen island with waterfall edge countertop — St. Catharines",
      title: "Custom Kitchen Island"
    }
  ];

  return (
    <>
      <SEOMeta
        title="Kitchen Renovations Niagara | Modern & Luxury Kitchen Remodels"
        description="Luxury kitchen renovations in Niagara. Modern design, expert remodels, and full kitchen renovations by Extreme Renovations. Free consult: 905-687-2208."
        keywords="kitchen renovations Niagara, kitchen remodels, kitchen remodeling near St Catharines ON, modern kitchen remodel contractor, kitchen remodeling Thorold ON, Niagara kitchens, kitchen remodeling cost, Kitchen Studio Niagara, home renovations Niagara region"
        canonicalUrl="https://www.extreme-renovations.ca/kitchen-renovations-niagara/"
        ogImage={modernKitchenIsland}
        schemaType="Service"
        schemaData={{
          name: "Kitchen Renovations | Extreme Renovations",
          serviceType: "Kitchen Renovation",
          areaServed: "Niagara",
          keywords: [
            "kitchen renovations Niagara",
            "modern kitchen remodel",
            "kitchen remodeling St Catharines",
            "kitchen remodeling Thorold",
            "Niagara kitchens",
            "Kitchen Studio Niagara",
            "home renovations Niagara region",
            "kitchen remodeling ideas",
            "kitchen remodeling cost"
          ]
        }}
      />
      
      <LocalBusinessSchema
        locationName="Niagara"
        addressLocality="Welland"
        addressRegion="ON"
        postalCode="L3B 3P2"
        streetAddress="10 Federal Road"
        description="Modern kitchen renovations and remodels across Niagara. Design-driven builds by Extreme Renovations in Welland."
        services={["Kitchen Renovations", "Modern Kitchen Remodels", "Custom Cabinetry", "Kitchen Design"]}
      />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main>
          {/* Breadcrumbs */}
          <Breadcrumbs />

          {/* Hero Section */}
          <section className="relative py-20 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                  Kitchen Renovations in Niagara — Modern, Functional, and Beautiful
                </h1>
                <p className="text-xl md:text-2xl text-muted-foreground mb-8">
                  Extreme Renovations builds kitchens that make cooking fun again
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="text-lg" asChild>
                    <a href="tel:905-687-2208">
                      <Phone className="mr-2 h-5 w-5" />
                      Call 905-687-2208
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg" asChild>
                    <a href="/contact">Free Consultation</a>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Your kitchen should be more than a place to cook—it should be the heart of your home. At Extreme Renovations, we create modern kitchens across Niagara that combine style, function, and craftsmanship. Whether you want an open-concept layout, quartz countertops, or sleek cabinetry with soft-close drawers, we handle everything from design to installation. Our team specializes in full kitchen remodels and luxury upgrades—executed on time, within budget, and without the chaos. Looking for a complete home transformation? Check out our <Link to="/services/bathroom-renovations" className="text-primary hover:underline font-medium">bathroom renovation services</Link> too.
                </p>
              </div>
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Why Niagara Homeowners Trust Extreme Renovations
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                <Card className="border-2">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-semibold mb-4">Design-First Thinking (Form Meets Function)</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>We optimize layouts for flow, storage, and natural light</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>3D renderings help you visualize before we build</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Expert material guidance and trend insights</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-semibold mb-4">Transparent Pricing, No Surprise Costs</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Detailed, itemized estimates upfront</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Budget-smart solutions without sacrificing quality</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Clear communication throughout the project</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-semibold mb-4">Trusted Local Trades, Clean Worksites</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Based in Welland, serving Niagara for years</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Floor protection and dust barriers standard</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span>Courteous crews who respect your home</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Full Kitchen Renovations */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Full Kitchen Renovations (Gut-to-Gorgeous)
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  We handle everything—from knocking down walls to wiring new lighting. Our full kitchen renovations include services from our certified <Link to="/services/electrician" className="text-primary hover:underline font-medium">electricians</Link>, <Link to="/services/plumber" className="text-primary hover:underline font-medium">plumbers</Link>, and <Link to="/services/carpenter" className="text-primary hover:underline font-medium">carpenters</Link>. With expert <Link to="/services/flooring" className="text-primary hover:underline font-medium">flooring installation</Link> and custom tilework, we transform your space into a modern, functional masterpiece. We ensure smooth coordination between all trades, so your project stays on time.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Layout redesign and space optimization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Custom cabinetry with soft-close drawers</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Quartz and granite countertops</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Designer tile backsplashes</span>
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Premium flooring installation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Kitchen islands with seating</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Plumbing and electrical upgrades</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>LED lighting and fixtures</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Modern Kitchen Remodels */}
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Modern Kitchen Remodels (Your Lifestyle, Upgraded)
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Open up your space with an island, add under-cabinet lighting, install a sleek hood vent, and upgrade to smart appliances. We integrate timeless design with modern tech and durability, ensuring your kitchen performs as beautifully as it looks.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Open-concept layouts for better flow</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Waterfall quartz countertops</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Smart appliances and tech integration</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Hidden storage solutions</span>
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Soft-close cabinetry throughout</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>LED under-cabinet and accent lighting</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Custom pantry organization</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Designer hardware and fixtures</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Small Kitchen & Condo */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Small Kitchen Makeovers & Condo Upgrades
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Even tight spaces deserve great design. We specialize in maximizing storage, using reflective finishes and efficient layouts to make smaller kitchens feel open and practical.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Space-saving layouts and designs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Custom storage hacks and solutions</span>
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Stylish finishes on sensible budgets</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span>Compact, efficient appliances</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Gallery */}
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Recent Kitchen Renovations in Niagara
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {galleryImages.map((image, index) => (
                  <Card key={index} className="overflow-hidden group">
                    <OptimizedImage
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <CardContent className="p-4">
                      <p className="font-semibold text-center">{image.title}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* 5-Step Process */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Our 5-Step Kitchen Renovation Process
              </h2>
              <div className="max-w-4xl mx-auto space-y-6">
                {[
                  {
                    step: 1,
                    title: "Design Consultation & Estimate",
                    description: "Measure, 3D concepts, and a line-item quote tailored to your vision and budget."
                  },
                  {
                    step: 2,
                    title: "Layout & Material Selection",
                    description: "Choose cabinets, countertops, flooring, lighting, and finishes. We lock fixtures and set a realistic start date."
                  },
                  {
                    step: 3,
                    title: "Demolition & Prep",
                    description: "Dust control, careful removal, plumbing/electrical rough-ins, and structural prep work."
                  },
                  {
                    step: 4,
                    title: "Cabinetry, Countertops & Finishes",
                    description: "Professional installation of cabinets, counters, backsplash, flooring, trim, and accessories."
                  },
                  {
                    step: 5,
                    title: "Final Walkthrough & Warranty",
                    description: "Punch-list perfection, quality check, cleanup, and documented care tips with warranty."
                  }
                ].map((item) => (
                  <Card key={item.step} className="border-l-4 border-l-primary">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                          {item.step}
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                          <p className="text-muted-foreground">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Kitchen Renovation Costs (Niagara Region)
              </h2>
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                <Card>
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-semibold mb-3">Compact Kitchen Refresh</h3>
                    <p className="text-3xl font-bold text-primary mb-4">$12k–$25k+</p>
                    <p className="text-sm text-muted-foreground">CAD</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-primary">
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-semibold mb-3">Mid-Range Full Remodel</h3>
                    <p className="text-3xl font-bold text-primary mb-4">$25k–$50k+</p>
                    <p className="text-sm text-muted-foreground">CAD</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <h3 className="text-xl font-semibold mb-3">Luxury Custom Kitchen</h3>
                    <p className="text-3xl font-bold text-primary mb-4">$60k–$120k+</p>
                    <p className="text-sm text-muted-foreground">CAD</p>
                  </CardContent>
                </Card>
              </div>
              <p className="text-center text-muted-foreground mt-8 max-w-3xl mx-auto">
                Pricing varies by layout, cabinetry style, countertops, and appliance choices. We'll price yours clearly before we start—no surprise costs.
              </p>
            </div>
          </section>

          {/* Service Area */}
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Service Area</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Proudly serving homeowners across the Niagara Region
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  {[
                    "Welland",
                    "St. Catharines",
                    "Niagara Falls",
                    "Niagara-on-the-Lake",
                    "Thorold",
                    "Fort Erie",
                    "Port Colborne",
                    "Pelham",
                    "Grimsby",
                    "Lincoln",
                    "West Lincoln"
                  ].map((city) => (
                    <span key={city} className="px-4 py-2 bg-primary/10 rounded-full text-sm font-medium">
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* FAQs */}
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <FAQ
                title="Frequently Asked Questions"
                description="Common questions about kitchen renovations in Niagara"
                faqs={faqs}
              />
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Design Your Dream Kitchen?
                </h2>
                <p className="text-xl mb-8 opacity-90">
                  Transform your kitchen into the heart of your home with Extreme Renovations
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                  <Button size="lg" variant="secondary" className="text-lg" asChild>
                    <a href="tel:905-687-2208">
                      <Phone className="mr-2 h-5 w-5" />
                      Call 905-687-2208
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg bg-transparent border-2 hover:bg-primary-foreground/10" asChild>
                    <a href="/contact">Request Free Consultation</a>
                  </Button>
                </div>
                <p className="flex items-center justify-center gap-2 text-sm opacity-75">
                  <Clock className="h-4 w-4" />
                  Weekdays 9:00 AM – 5:00 PM
                </p>
              </div>
            </div>
          </section>

          {/* Permits Notice */}
          <section className="py-12 bg-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Card className="border-l-4 border-l-primary bg-muted/30">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-3">Permits & Building Codes</h3>
                    <p className="text-muted-foreground">
                      If your kitchen remodel involves plumbing/electrical changes or layout modifications, permits and inspections may be required. We coordinate with your municipality in Niagara (e.g., Welland, St. Catharines, Niagara Falls) and ensure all work meets Ontario Building Code requirements.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default KitchenRenovations;
